// Visual identity + role taxonomy for the Team Visualization views
// (PRODOPSAI-534; per-role sections PRODOPSAI-543). Pure, module-level
// helpers so the components stay flat (SonarCloud S3776 / S7721) and
// everything is unit-testable.
//
// Colours come from the design-system `@theme` chart palette (chart-1..10 in
// index.css). All class strings are written as complete literals so Tailwind's
// content scanner generates them (dynamic `bg-chart-${n}` would be purged).

export interface TeamAccent {
  /** Left identity bar background. */
  bar: string;
  /** Monogram / icon tile background tint + text. */
  tileBg: string;
  tileText: string;
  /** Member initials-badge tint + text. */
  badgeBg: string;
  badgeText: string;
}

// One entry per chart colour, ordered for pleasant rotation across a grid.
const TEAM_ACCENTS: readonly TeamAccent[] = [
  { bar: 'bg-chart-1', tileBg: 'bg-chart-1/10', tileText: 'text-chart-1', badgeBg: 'bg-chart-1/10', badgeText: 'text-chart-1' },
  { bar: 'bg-chart-2', tileBg: 'bg-chart-2/10', tileText: 'text-chart-2', badgeBg: 'bg-chart-2/10', badgeText: 'text-chart-2' },
  { bar: 'bg-chart-3', tileBg: 'bg-chart-3/10', tileText: 'text-chart-3', badgeBg: 'bg-chart-3/10', badgeText: 'text-chart-3' },
  { bar: 'bg-chart-4', tileBg: 'bg-chart-4/10', tileText: 'text-chart-4', badgeBg: 'bg-chart-4/10', badgeText: 'text-chart-4' },
  { bar: 'bg-chart-8', tileBg: 'bg-chart-8/10', tileText: 'text-chart-8', badgeBg: 'bg-chart-8/10', badgeText: 'text-chart-8' },
  { bar: 'bg-chart-9', tileBg: 'bg-chart-9/10', tileText: 'text-chart-9', badgeBg: 'bg-chart-9/10', badgeText: 'text-chart-9' },
  { bar: 'bg-chart-7', tileBg: 'bg-chart-7/10', tileText: 'text-chart-7', badgeBg: 'bg-chart-7/10', badgeText: 'text-chart-7' },
  { bar: 'bg-chart-5', tileBg: 'bg-chart-5/10', tileText: 'text-chart-5', badgeBg: 'bg-chart-5/10', badgeText: 'text-chart-5' },
  { bar: 'bg-chart-6', tileBg: 'bg-chart-6/10', tileText: 'text-chart-6', badgeBg: 'bg-chart-6/10', badgeText: 'text-chart-6' },
  { bar: 'bg-chart-10', tileBg: 'bg-chart-10/10', tileText: 'text-chart-10', badgeBg: 'bg-chart-10/10', badgeText: 'text-chart-10' },
];

// Accent encodes the team's REGION (PRODOPSAI-535) rather than a per-team
// hash, so colour carries meaning and the palette stays calm under the
// region-grouped layout. Each region maps to one fixed palette entry.
const REGION_ACCENT_INDEX: Readonly<Record<string, number>> = {
  Global: 0, // chart-1 (blue)
  SWE: 1, // chart-2 (emerald)
  UK: 4, // chart-8 (indigo)
  US: 6, // chart-7 (amber)
};
const UNASSIGNED_ACCENT_INDEX = 5; // chart-9 (teal) — also any unknown region

/** Accent for a team, keyed by its region. Unknown / missing region → a calm
 *  neutral-teal fallback. */
export function teamAccent(regionName: string | null | undefined): TeamAccent {
  if (regionName === null || regionName === undefined || regionName.trim() === '') {
    return TEAM_ACCENTS[UNASSIGNED_ACCENT_INDEX];
  }
  const index = REGION_ACCENT_INDEX[regionName];
  return TEAM_ACCENTS[index ?? UNASSIGNED_ACCENT_INDEX];
}

/** Single-character monogram for a team's icon tile (drops a leading "Team "). */
export function teamMonogram(name: string): string {
  const cleaned = name.replace(/^team\s+/i, '').trim();
  const source = cleaned.length > 0 ? cleaned : name.trim();
  const first = Array.from(source)[0] ?? '?';
  return first.toUpperCase();
}

// ── Role abbreviations (keyed by normalised displayName) ──────────────────────

const ROLE_ABBREVIATION: Readonly<Record<string, string>> = {
  'engineering manager': 'EM',
  'product owner': 'PO',
  'business analyst': 'BA',
  'ux/ui designer': 'UX',
  'solution architect': 'SA',
  'lead engineer': 'LE',
  engineer: 'ENG',
  'back-end developer': 'BE',
  'front-end engineer': 'FE',
  sdet: 'SDET',
  'sre / devops': 'SRE',
  'functional consultant': 'FC',
};

/** Fallback: uppercase initials of the words, max 4 chars. */
function deriveAbbreviation(displayName: string): string {
  const letters = displayName
    .split(/[\s/]+/)
    .filter((w) => w.length > 0)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return letters.slice(0, 4) || '?';
}

/** Compact role code (EM, ENG, UX …) for a role displayName. */
export function roleAbbreviation(displayName: string): string {
  return ROLE_ABBREVIATION[displayName.trim().toLowerCase()] ?? deriveAbbreviation(displayName);
}

// ── Per-role accent (PRODOPSAI-543) ───────────────────────────────────────────
//
// Static role-slug → accent map. Deterministic, explicit, and free of any
// hash arithmetic (SonarCloud S5547 / S2245 flag custom `* 31 + charCode`
// hashes as weak-crypto candidates even when used purely for UI colour
// selection). All class strings are complete literals so Tailwind's content
// scanner generates them. Slugs come from `slugify(displayName)` in
// `team-detail-helpers.ts` — see the catalog in `backend/src/config/database.ts`.

export interface RoleAccent {
  text: string;
  tint: string;
}

const ROLE_ACCENT_BY_SLUG: Readonly<Record<string, RoleAccent>> = {
  engineering_manager: { tint: 'bg-chart-7/10', text: 'text-chart-7' }, // amber (leadership)
  product_owner: { tint: 'bg-chart-3/10', text: 'text-chart-3' }, // emerald
  product_manager: { tint: 'bg-chart-3/10', text: 'text-chart-3' },
  business_analyst: { tint: 'bg-chart-6/10', text: 'text-chart-6' }, // pink/rose
  solution_architect: { tint: 'bg-chart-8/10', text: 'text-chart-8' }, // indigo
  agile_coach: { tint: 'bg-chart-7/10', text: 'text-chart-7' }, // amber (leadership)
  lead_engineer: { tint: 'bg-chart-1/10', text: 'text-chart-1' }, // blue
  engineer: { tint: 'bg-chart-1/10', text: 'text-chart-1' },
  back_end_developer: { tint: 'bg-chart-1/10', text: 'text-chart-1' },
  backend_developer: { tint: 'bg-chart-1/10', text: 'text-chart-1' },
  front_end_engineer: { tint: 'bg-chart-1/10', text: 'text-chart-1' },
  frontend_engineer: { tint: 'bg-chart-1/10', text: 'text-chart-1' },
  sdet: { tint: 'bg-chart-9/10', text: 'text-chart-9' }, // teal
  ux_ui_designer: { tint: 'bg-chart-5/10', text: 'text-chart-5' }, // purple
  sre_devops: { tint: 'bg-chart-4/10', text: 'text-chart-4' }, // orange
  platform_support_developer: { tint: 'bg-chart-4/10', text: 'text-chart-4' },
  functional_consultant: { tint: 'bg-chart-2/10', text: 'text-chart-2' }, // green
};

const DEFAULT_ROLE_ACCENT: RoleAccent = {
  tint: 'bg-muted',
  text: 'text-muted-foreground',
};

/** Stable deterministic accent for a role keyed by its slug. Unknown slugs
 *  fall back to a calm neutral. */
export function roleAccent(roleSlug: string): RoleAccent {
  return ROLE_ACCENT_BY_SLUG[roleSlug] ?? DEFAULT_ROLE_ACCENT;
}
